/**
 * Linear expressions and equations in a single variable, over exact rationals.
 *
 * A LinearExpr is `coeff * x + constant`. This is deliberately the *canonical*
 * form: any product of binomials in Algebra I's first nine weeks distributes
 * down to this shape, so the whole equation-solving curriculum reduces to
 * arithmetic on two Rationals per side.
 *
 * The solver classifies every equation into one of three categories and, for
 * the ONE case, returns the exact solution. It is used both to build problems
 * (generators assemble expressions) and to verify them independently.
 */

import { Rational } from "./rational";

export type SolutionCategory = "one" | "none" | "infinite";

export interface LinearSolution {
  readonly category: SolutionCategory;
  /** Present only when category === "one". */
  readonly value?: Rational;
}

export class LinearExpr {
  /** Coefficient of the variable. */
  readonly coeff: Rational;
  /** Constant term. */
  readonly constant: Rational;

  constructor(coeff: Rational, constant: Rational) {
    this.coeff = coeff;
    this.constant = constant;
  }

  static readonly ZERO = new LinearExpr(Rational.ZERO, Rational.ZERO);

  /** The bare variable, x. */
  static variable(): LinearExpr {
    return new LinearExpr(Rational.ONE, Rational.ZERO);
  }

  /** A constant expression with no variable term. */
  static constantExpr(c: Rational): LinearExpr {
    return new LinearExpr(Rational.ZERO, c);
  }

  add(o: LinearExpr): LinearExpr {
    return new LinearExpr(this.coeff.add(o.coeff), this.constant.add(o.constant));
  }

  sub(o: LinearExpr): LinearExpr {
    return new LinearExpr(this.coeff.sub(o.coeff), this.constant.sub(o.constant));
  }

  /** Scale the whole expression by a rational factor (distribution). */
  scale(k: Rational): LinearExpr {
    return new LinearExpr(this.coeff.mul(k), this.constant.mul(k));
  }

  neg(): LinearExpr {
    return new LinearExpr(this.coeff.neg(), this.constant.neg());
  }

  /** Evaluate at a specific value of the variable. */
  evaluate(x: Rational): Rational {
    return this.coeff.mul(x).add(this.constant);
  }

  hasVariable(): boolean {
    return !this.coeff.isZero();
  }

  equals(o: LinearExpr): boolean {
    return this.coeff.equals(o.coeff) && this.constant.equals(o.constant);
  }
}

export class LinearEquation {
  readonly left: LinearExpr;
  readonly right: LinearExpr;

  constructor(left: LinearExpr, right: LinearExpr) {
    this.left = left;
    this.right = right;
  }

  /**
   * Solve and classify.
   *
   * Move everything to one side: (leftCoeff - rightCoeff) x = (rightConst - leftConst).
   * Let a = coefficient difference, b = constant difference.
   *   a != 0            -> exactly one solution, x = b / a
   *   a == 0 and b == 0 -> both sides identical -> infinitely many
   *   a == 0 and b != 0 -> contradiction        -> no solution
   */
  solve(): LinearSolution {
    const a = this.left.coeff.sub(this.right.coeff);
    const b = this.right.constant.sub(this.left.constant);

    if (!a.isZero()) {
      return { category: "one", value: b.div(a) };
    }
    if (b.isZero()) {
      return { category: "infinite" };
    }
    return { category: "none" };
  }

  /**
   * Independent check that a candidate value satisfies this equation.
   * Used by the validation layer - evaluates both sides rather than trusting
   * whatever produced the equation.
   */
  isSatisfiedBy(x: Rational): boolean {
    return this.left.evaluate(x).equals(this.right.evaluate(x));
  }
}
